import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  LayoutDashboard, UtensilsCrossed, Calendar, ShoppingBag, MessageSquare, Star, Settings,
  LogOut, Search, Filter, Trash2, RotateCcw, Mail, Phone, Clock, MapPin, ChevronDown,
  ChevronRight, MoreHorizontal, Send, X, Check, AlertTriangle, Package, Users, CreditCard,
  QrCode, Eye, Undo2, XCircle, Loader2, ArrowLeft, ChefHat, Truck, Timer,
  BookOpen, Download, FileText, Plus, ChevronLeft
} from 'lucide-react';
import {
  login as apiLogin, getMe, getAdminStats, getAdminOrders, getAdminReservations,
  getAdminContacts, getAdminMenu, getAdminReviews, getAdminTables, getAdminTableOrders,
  updateOrderStatus, updateReservationStatus, deleteOrder, deleteReservation, deleteContact,
  deleteReview, deleteMenuItem, updateMenuItem, createMenuItem, createReview, toggleReview,
  getTrash, restoreFromTrash, permanentDeleteFromTrash, emptyTrash, sendAdminEmail,
  sendOrderTime, markOrderPaidCollected, updateOrderNote, updateReservationNote,
  updateTableStatus, updateTableOrderStatus, payTableOrderFull, payTableOrderItems,
  getTableBill, updateContactStatus, updateContactNote, getSettings, updateSettings,
  getAllCategories, createCategory, updateCategory, deleteCategory,
  getKassenbuch, getKassenbuchSummary, createKassenbuchEntry, deleteKassenbuchEntry,
  sendKassenbuchReport, downloadKassenbuchPdf, getKassenbuchReports
} from '@/lib/api';
import { getWhatsAppLinkToCustomer, generateOrderConfirmation, generateReservationConfirmation } from '@/components/WhatsAppFloat';

// ━━━ STATUS HELPERS ━━━
const STATUS_MAP = {
  new: { label: 'Neu', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  confirmed: { label: 'Bestätigt', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  in_zubereitung: { label: 'Zubereitung', color: 'bg-sky-100 text-sky-800 border-sky-200' },
  ready: { label: 'Fertig', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  bezahlt_abgeholt: { label: 'Bezahlt', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled: { label: 'Storniert', color: 'bg-rose-100 text-rose-800 border-rose-200' },
  done: { label: 'Erledigt', color: 'bg-stone-100 text-stone-600 border-stone-200' },
  read: { label: 'Gelesen', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  replied: { label: 'Beantwortet', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const getStatus = (s) => STATUS_MAP[s] || { label: s, color: 'bg-stone-100 text-stone-600 border-stone-200' };

const TRASH_TYPE_LABELS = { orders: 'Bestellungen', reservations: 'Reservierungen', contacts: 'Nachrichten', menu: 'Speisekarte', reviews: 'Bewertungen' };

// ━━━ LOGIN SCREEN ━━━
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await apiLogin({ email, password });
      localStorage.setItem('li-token', data.token);
      onLogin(data.user);
    } catch { toast.error('Ungültige Anmeldedaten'); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-100 p-4" data-testid="admin-login">
      <Card className="w-full max-w-md shadow-xl border-stone-200">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-1 bg-amber-600 mx-auto mb-3 rounded-full" />
          <CardTitle className="font-serif text-2xl text-stone-800">Little Italy Admin</CardTitle>
          <p className="text-sm text-stone-500 mt-1">Melden Sie sich an, um fortzufahren</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input data-testid="login-email" type="email" placeholder="E-Mail" value={email} onChange={e => setEmail(e.target.value)} required />
            <Input data-testid="login-password" type="password" placeholder="Passwort" value={password} onChange={e => setPassword(e.target.value)} required />
            <Button data-testid="login-submit" type="submit" className="w-full bg-amber-700 hover:bg-amber-800" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Anmelden'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ━━━ EMAIL COMPOSE MODAL ━━━
function EmailComposeModal({ open, onClose, prefill = {} }) {
  const [to, setTo] = useState(prefill.to || '');
  const [toName, setToName] = useState(prefill.toName || '');
  const [subject, setSubject] = useState(prefill.subject || '');
  const [message, setMessage] = useState(prefill.message || '');
  const [contextRef, setContextRef] = useState(prefill.contextRef || '');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setTo(prefill.to || ''); setToName(prefill.toName || ''); setSubject(prefill.subject || ''); setMessage(prefill.message || ''); setContextRef(prefill.contextRef || ''); }
  }, [open, prefill]);

  const handleSend = async () => {
    if (!to || !subject || !message) { toast.error('Bitte alle Pflichtfelder ausfüllen'); return; }
    setSending(true);
    try {
      await sendAdminEmail({ to, to_name: toName, subject, message, context_ref: contextRef });
      toast.success(`E-Mail an ${to} gesendet`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler beim Senden'); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="email-compose-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-amber-700" /> Kunde per E-Mail kontaktieren</DialogTitle>
          <DialogDescription>Senden Sie eine Nachricht an den Kunden</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-stone-500 mb-1 block">An (E-Mail) *</label><Input value={to} onChange={e => setTo(e.target.value)} placeholder="kunde@email.de" data-testid="email-to" /></div>
            <div><label className="text-xs font-medium text-stone-500 mb-1 block">Name</label><Input value={toName} onChange={e => setToName(e.target.value)} placeholder="Name" /></div>
          </div>
          {contextRef && <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-500"><strong>Referenz:</strong> {contextRef}</div>}
          <div><label className="text-xs font-medium text-stone-500 mb-1 block">Betreff *</label><Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Betreff" data-testid="email-subject" /></div>
          <div><label className="text-xs font-medium text-stone-500 mb-1 block">Nachricht *</label><Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Ihre Nachricht..." rows={6} data-testid="email-message" className="resize-none" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Abbrechen</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-amber-700 hover:bg-amber-800" data-testid="email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ━━━ TIME ESTIMATE MODAL ━━━
function TimeEstimateModal({ open, onClose, order, onSend }) {
  const [minutes, setMinutes] = useState(30);
  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    setSending(true);
    try { await sendOrderTime(order.id, minutes); toast.success(`Fertigstellungszeit (${minutes} Min.) gesendet`); onSend(); onClose(); }
    catch (e) { toast.error('Fehler beim Senden'); }
    setSending(false);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="time-estimate-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Timer className="w-5 h-5 text-amber-700" />Fertigstellungszeit senden</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Senden Sie dem Kunden die geschätzte Wartezeit per E-Mail.</p>
        <div className="flex items-center gap-3 py-3">
          {[15, 20, 30, 45, 60].map(m => (
            <button key={m} onClick={() => setMinutes(m)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${minutes === m ? 'bg-amber-700 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>{m}</button>
          ))}
        </div>
        <div className="flex items-center gap-2"><Input type="number" value={minutes} onChange={e => setMinutes(parseInt(e.target.value) || 0)} min={5} max={120} className="w-24" /><span className="text-sm text-stone-500">Minuten</span></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-amber-700 hover:bg-amber-800">{sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Senden</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ━━━ TABLE BILL MODAL ━━━
function TableBillModal({ open, onClose, tableNumber, onUpdate }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (open && tableNumber) {
      setLoading(true);
      getTableBill(tableNumber).then(({ data }) => setBill(data)).catch(() => toast.error('Fehler')).finally(() => setLoading(false));
    }
  }, [open, tableNumber]);
  const handlePayFull = async (oid) => {
    try { await payTableOrderFull(oid); toast.success('Vollständig bezahlt'); const { data } = await getTableBill(tableNumber); setBill(data); onUpdate(); } catch { toast.error('Fehler'); }
  };
  const handlePayItem = async (oid, idx) => {
    try { await payTableOrderItems(oid, [idx]); toast.success('Artikel bezahlt'); const { data } = await getTableBill(tableNumber); setBill(data); onUpdate(); } catch { toast.error('Fehler'); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="table-bill-modal">
        <DialogHeader><DialogTitle>Tisch {tableNumber} - Rechnung</DialogTitle><DialogDescription>Offene Bestellungen und Zahlungsstatus</DialogDescription></DialogHeader>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-700" /></div> : bill && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-stone-50 rounded-lg p-3"><p className="text-xs text-stone-500">Gesamt</p><p className="text-lg font-bold text-stone-800">{bill.total?.toFixed(2)} &euro;</p></div>
              <div className="bg-emerald-50 rounded-lg p-3"><p className="text-xs text-emerald-600">Bezahlt</p><p className="text-lg font-bold text-emerald-700">{bill.paid?.toFixed(2)} &euro;</p></div>
              <div className="bg-amber-50 rounded-lg p-3"><p className="text-xs text-amber-600">Offen</p><p className="text-lg font-bold text-amber-700">{bill.remaining?.toFixed(2)} &euro;</p></div>
            </div>
            {bill.orders?.map((o, idx) => (
              <Card key={o.id || idx} className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-stone-600">Bestellung #{(o.id || '').slice(0,6)}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getStatus(o.status).color}>{getStatus(o.status).label}</Badge>
                      {o.status !== 'bezahlt' && <Button size="sm" variant="outline" onClick={() => handlePayFull(o.id)} className="text-xs h-7"><CreditCard className="w-3 h-3 mr-1" />Alles bezahlen</Button>}
                    </div>
                  </div>
                  {o.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-t border-stone-100">
                      <span className={`text-sm ${o.paid_items?.[i] ? 'line-through text-stone-400' : 'text-stone-700'}`}>{item.quantity}x {item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium tabular-nums">{item.price?.toFixed(2)} &euro;</span>
                        {!o.paid_items?.[i] && o.status !== 'bezahlt' && <button onClick={() => handlePayItem(o.id, i)} className="text-xs text-amber-700 hover:text-amber-800 font-medium">Bezahlen</button>}
                        {o.paid_items?.[i] && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ━━━ COMPACT ACTION DROPDOWN ━━━
function ActionMenu({ children, label = "Aktionen" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)} className="h-8 text-xs gap-1">
        <MoreHorizontal className="w-3.5 h-3.5" /><span className="hidden sm:inline">{label}</span>
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[180px]" onClick={() => setOpen(false)}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}
function ActionItem({ icon: Icon, label, onClick, variant = 'default', className = '' }) {
  const colors = { default: 'text-stone-700 hover:bg-stone-50', danger: 'text-rose-600 hover:bg-rose-50', success: 'text-emerald-700 hover:bg-emerald-50', info: 'text-sky-700 hover:bg-sky-50' };
  return <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${colors[variant]} ${className}`}>{Icon && <Icon className="w-4 h-4" />}{label}</button>;
}

// ━━━ FILTER BAR ━━━
function FilterBar({ search, setSearch, statusFilter, setStatusFilter, statusOptions, placeholder = "Suchen...", children }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder} className="pl-9 h-9 text-sm" data-testid="filter-search" />
      </div>
      {statusOptions && (
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-status"><Filter className="w-3.5 h-3.5 mr-1.5 text-stone-400" /><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {children}
    </div>
  );
}

// ━━━ KASSENBUCH TAB ━━━
const MONTH_NAMES_DE = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function KassenbuchTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ beschreibung: '', einnahme: '', steuersatz: 19 });

  const loadKassenbuch = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes, rRes] = await Promise.all([
        getKassenbuch(year, month),
        getKassenbuchSummary(year, month),
        getKassenbuchReports()
      ]);
      setEntries(eRes.data);
      setSummary(sRes.data);
      setReports(rRes.data);
    } catch (e) { console.error('Kassenbuch load error', e); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { loadKassenbuch(); }, [loadKassenbuch]);

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleSendReport = async () => {
    setSending(true);
    try {
      const { data } = await sendKassenbuchReport(year, month);
      toast.success(data.message || 'Bericht gesendet');
      loadKassenbuch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Senden'); }
    setSending(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await downloadKassenbuchPdf(year, month);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kassenbuch_${MONTH_NAMES_DE[month]}_${year}_LittleItaly.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (e) { toast.error('Fehler beim Download'); }
    setDownloading(false);
  };

  const handleAddEntry = async () => {
    if (!newEntry.beschreibung || !newEntry.einnahme) { toast.error('Bitte alle Felder ausfüllen'); return; }
    try {
      await createKassenbuchEntry({
        beschreibung: newEntry.beschreibung,
        einnahme: parseFloat(newEntry.einnahme),
        steuersatz: parseInt(newEntry.steuersatz),
        datum: `${year}-${String(month).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}T${new Date().toTimeString().slice(0, 8)}`
      });
      toast.success('Eintrag hinzugefügt');
      setNewEntry({ beschreibung: '', einnahme: '', steuersatz: 19 });
      setShowAddEntry(false);
      loadKassenbuch();
    } catch (e) { toast.error('Fehler beim Hinzufügen'); }
  };

  const handleDeleteEntry = async (id) => {
    try { await deleteKassenbuchEntry(id); toast.success('Eintrag gelöscht'); loadKassenbuch(); }
    catch { toast.error('Fehler'); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { const dt = new Date(d); return dt.toLocaleDateString('de-DE'); } catch { return d; }
  };

  const lastReport = reports.find(r => r.year === year && r.month === month);

  return (
    <div className="space-y-4" data-testid="kassenbuch-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-serif text-stone-800 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-amber-700" /> Kassenbuch
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddEntry(true)} data-testid="add-entry-btn">
            <Plus className="w-4 h-4 mr-1" /> Eintrag
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            PDF
          </Button>
          <Button size="sm" onClick={handleSendReport} disabled={sending} className="bg-amber-700 hover:bg-amber-800" data-testid="send-report-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
            Bericht senden
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={handlePrevMonth} data-testid="prev-month-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <p className="text-lg font-serif font-medium text-stone-800" data-testid="current-month">{MONTH_NAMES_DE[month]} {year}</p>
              {lastReport && <p className="text-xs text-emerald-600 mt-1">Letzter Versand: {formatDate(lastReport.sent_at)}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={handleNextMonth} data-testid="next-month-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-stone-800" data-testid="summary-brutto">{summary.brutto_gesamt?.toFixed(2)} &euro;</p>
              <p className="text-xs text-stone-500 mt-1">Brutto Gesamt</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700" data-testid="summary-netto">{summary.netto_gesamt?.toFixed(2)} &euro;</p>
              <p className="text-xs text-stone-500 mt-1">Netto Gesamt</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700" data-testid="summary-mwst">{summary.mwst_gesamt?.toFixed(2)} &euro;</p>
              <p className="text-xs text-stone-500 mt-1">MwSt Gesamt</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-stone-600" data-testid="summary-count">{summary.anzahl}</p>
              <p className="text-xs text-stone-500 mt-1">Buchungen</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Entries Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-700" /></div>
      ) : entries.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-400">Keine Einträge für {MONTH_NAMES_DE[month]} {year}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kassenbuch-table">
              <thead>
                <tr className="bg-stone-800 text-white text-xs">
                  <th className="px-3 py-2.5 text-left font-medium">Nr.</th>
                  <th className="px-3 py-2.5 text-left font-medium">Datum</th>
                  <th className="px-3 py-2.5 text-left font-medium">Beleg-Nr.</th>
                  <th className="px-3 py-2.5 text-left font-medium">Beschreibung</th>
                  <th className="px-3 py-2.5 text-right font-medium">Einnahme</th>
                  <th className="px-3 py-2.5 text-center font-medium">MwSt</th>
                  <th className="px-3 py-2.5 text-right font-medium">Netto</th>
                  <th className="px-3 py-2.5 text-right font-medium">MwSt Betrag</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr key={e.id} className={idx % 2 === 0 ? 'bg-stone-50/50' : 'bg-white'} data-testid={`entry-${e.id}`}>
                    <td className="px-3 py-2 text-stone-500 tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-2 tabular-nums">{formatDate(e.datum)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-stone-600">{e.belegnummer}</td>
                    <td className="px-3 py-2 max-w-[280px] truncate" title={e.beschreibung}>{e.beschreibung}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{e.einnahme?.toFixed(2)} &euro;</td>
                    <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px] px-1.5">{e.steuersatz}%</Badge></td>
                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{e.netto?.toFixed(2)} &euro;</td>
                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{e.mwst_betrag?.toFixed(2)} &euro;</td>
                    <td className="px-2 py-2">
                      {e.order_type === 'manuell' && (
                        <button onClick={() => handleDeleteEntry(e.id)} className="text-stone-400 hover:text-rose-600 transition-colors" data-testid={`delete-entry-${e.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* MwSt Breakdown */}
      {summary && (summary.mwst_19 > 0 || summary.mwst_7 > 0) && (
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-stone-600">MwSt-Aufschlüsselung</CardTitle></CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                <span className="text-stone-600">MwSt 19% (Essen)</span>
                <span className="font-medium">{summary.mwst_19?.toFixed(2)} &euro;</span>
              </div>
              <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                <span className="text-stone-600">MwSt 7% (Getränke)</span>
                <span className="font-medium">{summary.mwst_7?.toFixed(2)} &euro;</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Manueller Kassenbuch-Eintrag</DialogTitle><DialogDescription>Fügen Sie einen manuellen Eintrag zum Kassenbuch hinzu.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium mb-1 block">Beschreibung *</label><Input value={newEntry.beschreibung} onChange={e => setNewEntry(p => ({ ...p, beschreibung: e.target.value }))} placeholder="z.B. Bargeld-Einnahme" data-testid="entry-beschreibung" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Betrag (Brutto) *</label><Input type="number" step="0.01" min="0" value={newEntry.einnahme} onChange={e => setNewEntry(p => ({ ...p, einnahme: e.target.value }))} placeholder="0.00" data-testid="entry-einnahme" /></div>
              <div><label className="text-sm font-medium mb-1 block">MwSt-Satz</label>
                <Select value={String(newEntry.steuersatz)} onValueChange={v => setNewEntry(p => ({ ...p, steuersatz: parseInt(v) }))}>
                  <SelectTrigger data-testid="entry-steuersatz"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="19">19% – Essen</SelectItem>
                    <SelectItem value="7">7% – Getränke</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntry(false)}>Abbrechen</Button>
            <Button onClick={handleAddEntry} className="bg-amber-700 hover:bg-amber-800" data-testid="save-entry-btn">Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ━━━ MAIN DASHBOARD ━━━
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tables, setTables] = useState([]);
  const [tableOrders, setTableOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trash, setTrash] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [resSearch, setResSearch] = useState('');
  const [resStatusFilter, setResStatusFilter] = useState('all');
  const [contactSearch, setContactSearch] = useState('');

  // Modals
  const [emailModal, setEmailModal] = useState({ open: false, prefill: {} });
  const [timeModal, setTimeModal] = useState({ open: false, order: null });
  const [billModal, setBillModal] = useState({ open: false, tableNumber: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Check auth
  useEffect(() => {
    const t = localStorage.getItem('li-token');
    if (t) {
      getMe().then(({ data }) => { setUser(data); setLoading(false); }).catch(() => { localStorage.removeItem('li-token'); setLoading(false); });
    } else setLoading(false);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const [s, o, r, c, t, to, m, rv, ca] = await Promise.all([
        getAdminStats(), getAdminOrders(), getAdminReservations(), getAdminContacts(),
        getAdminTables(), getAdminTableOrders(), getAdminMenu(), getAdminReviews(), getAllCategories()
      ]);
      setStats(s.data); setOrders(o.data); setReservations(r.data); setContacts(c.data);
      setTables(t.data); setTableOrders(to.data); setMenuItems(m.data); setReviews(rv.data); setCategories(ca.data);
    } catch (e) { console.error('Load err', e); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadTrash = useCallback(async () => {
    try { const { data } = await getTrash(); setTrash(data); } catch { }
  }, []);

  useEffect(() => { if (activeTab === 'trash') loadTrash(); }, [activeTab, loadTrash]);

  const handleLogout = () => { localStorage.removeItem('li-token'); setUser(null); };

  // ━━━ FILTERED DATA ━━━
  const filteredOrders = useMemo(() => {
    let f = orders;
    if (orderStatusFilter !== 'all') f = f.filter(o => o.status === orderStatusFilter);
    if (orderSearch) { const q = orderSearch.toLowerCase(); f = f.filter(o => o.customer_name?.toLowerCase().includes(q) || o.customer_phone?.includes(q) || o.id?.includes(q)); }
    return f;
  }, [orders, orderStatusFilter, orderSearch]);

  const filteredReservations = useMemo(() => {
    let f = reservations;
    if (resStatusFilter !== 'all') f = f.filter(r => r.status === resStatusFilter);
    if (resSearch) { const q = resSearch.toLowerCase(); f = f.filter(r => r.name?.toLowerCase().includes(q) || r.phone?.includes(q) || r.email?.toLowerCase().includes(q)); }
    return f;
  }, [reservations, resStatusFilter, resSearch]);

  const filteredContacts = useMemo(() => {
    if (!contactSearch) return contacts;
    const q = contactSearch.toLowerCase();
    return contacts.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
  }, [contacts, contactSearch]);

  // ━━━ HANDLERS ━━━
  const handleOrderStatus = async (id, status) => { try { await updateOrderStatus(id, status); toast.success('Status aktualisiert'); loadData(); } catch { toast.error('Fehler'); } };
  const handleDeleteOrder = (id) => { setConfirmDialog({ open: true, title: 'Bestellung löschen', message: 'Die Bestellung wird in den Papierkorb verschoben.', onConfirm: async () => { try { await deleteOrder(id); toast.success('In Papierkorb verschoben'); loadData(); } catch { toast.error('Fehler'); } } }); };
  const handlePaidCollected = async (id) => { try { await markOrderPaidCollected(id); toast.success('Als bezahlt markiert'); loadData(); } catch { toast.error('Fehler'); } };
  const handleResStatus = async (id, status) => { try { await updateReservationStatus(id, status); toast.success('Aktualisiert'); loadData(); } catch { toast.error('Fehler'); } };
  const handleDeleteRes = (id) => { setConfirmDialog({ open: true, title: 'Reservierung löschen', message: 'In den Papierkorb verschieben?', onConfirm: async () => { try { await deleteReservation(id); toast.success('In Papierkorb verschoben'); loadData(); } catch { toast.error('Fehler'); } } }); };
  const handleDeleteContact = (id) => { setConfirmDialog({ open: true, title: 'Nachricht löschen', message: 'In den Papierkorb verschieben?', onConfirm: async () => { try { await deleteContact(id); toast.success('In Papierkorb verschoben'); loadData(); } catch { toast.error('Fehler'); } } }); };
  const handleDeleteReview = (id) => { setConfirmDialog({ open: true, title: 'Bewertung löschen', message: 'In den Papierkorb verschieben?', onConfirm: async () => { try { await deleteReview(id); toast.success('In Papierkorb verschoben'); loadData(); } catch { toast.error('Fehler'); } } }); };
  const handleDeleteMenu = (id) => { setConfirmDialog({ open: true, title: 'Gericht löschen', message: 'In den Papierkorb verschieben?', onConfirm: async () => { try { await deleteMenuItem(id); toast.success('In Papierkorb verschoben'); loadData(); } catch { toast.error('Fehler'); } } }); };
  const handleRestoreTrash = async (type, id) => { try { await restoreFromTrash(type, id); toast.success('Wiederhergestellt'); loadTrash(); loadData(); } catch { toast.error('Fehler'); } };
  const handlePermanentDelete = (type, id) => { setConfirmDialog({ open: true, title: 'Endgültig löschen', message: 'Dieses Element wird unwiderruflich gelöscht!', onConfirm: async () => { try { await permanentDeleteFromTrash(type, id); toast.success('Endgültig gelöscht'); loadTrash(); } catch { toast.error('Fehler'); } } }); };
  const handleEmptyTrash = () => { setConfirmDialog({ open: true, title: 'Papierkorb leeren', message: 'Alle Elemente im Papierkorb werden endgültig gelöscht!', onConfirm: async () => { try { await emptyTrash(); toast.success('Papierkorb geleert'); setTrash({}); } catch { toast.error('Fehler'); } } }); };

  const openEmailFor = (to, toName, subject, contextRef) => {
    setEmailModal({ open: true, prefill: { to, toName, subject, contextRef, message: '' } });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><Loader2 className="w-8 h-8 animate-spin text-amber-700" /></div>;
  if (!user) return <LoginScreen onLogin={setUser} />;

  const trashCount = Object.values(trash).reduce((a, arr) => a + arr.length, 0) || stats?.trash_count || 0;

  const NAV = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'orders', icon: ShoppingBag, label: 'Bestellungen', badge: stats?.new_orders },
    { id: 'reservations', icon: Calendar, label: 'Reservierungen', badge: stats?.new_reservations },
    { id: 'tables', icon: Users, label: 'Tische' },
    { id: 'qrcodes', icon: QrCode, label: 'QR-Codes' },
    { id: 'contacts', icon: MessageSquare, label: 'Nachrichten', badge: stats?.new_contacts },
    { id: 'menu', icon: UtensilsCrossed, label: 'Speisekarte' },
    { id: 'reviews', icon: Star, label: 'Bewertungen' },
    { id: 'kassenbuch', icon: BookOpen, label: 'Kassenbuch' },
    { id: 'trash', icon: Trash2, label: 'Papierkorb', badge: trashCount },
    { id: 'settings', icon: Settings, label: 'Einstellungen' },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Top Nav */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-screen-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-0.5 bg-amber-600 rounded-full" />
            <h1 className="font-serif text-lg text-stone-800">Little Italy</h1>
            <span className="text-xs text-stone-400 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-500 hidden md:inline">{user?.name || user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-56 min-h-[calc(100vh-56px)] bg-white border-r border-stone-200 p-3 sticky top-14" data-testid="admin-sidebar">
          <nav className="space-y-0.5">
            {NAV.map(n => (
              <button key={n.id} onClick={() => setActiveTab(n.id)} data-testid={`nav-${n.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === n.id ? 'bg-amber-50 text-amber-800 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}>
                <n.icon className="w-4 h-4 flex-shrink-0" />{n.label}
                {n.badge > 0 && <span className="ml-auto bg-amber-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium min-w-[18px] text-center">{n.badge}</span>}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile Nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-stone-200 flex overflow-x-auto px-1">
          {NAV.slice(0, 7).map(n => (
            <button key={n.id} onClick={() => setActiveTab(n.id)} className={`flex-1 min-w-[52px] flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] ${activeTab === n.id ? 'text-amber-700 font-medium' : 'text-stone-400'}`}>
              <div className="relative"><n.icon className="w-4 h-4" />{n.badge > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-600 rounded-full" />}</div>
              <span className="truncate">{n.label.slice(0, 8)}</span>
            </button>
          ))}
          <button onClick={() => setActiveTab('trash')} className={`flex-1 min-w-[52px] flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] ${activeTab === 'trash' ? 'text-amber-700 font-medium' : 'text-stone-400'}`}>
            <div className="relative"><Trash2 className="w-4 h-4" />{trashCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-600 rounded-full" />}</div>
            <span>Papierkorb</span>
          </button>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 pb-24 lg:pb-6 min-w-0" data-testid="admin-main">

          {/* ━━━ DASHBOARD ━━━ */}
          {activeTab === 'dashboard' && stats && (
            <div className="space-y-6">
              <h2 className="text-xl font-serif text-stone-800">Dashboard</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { l: 'Neue Bestellungen', v: stats.new_orders, c: 'text-amber-700', onClick: () => { setActiveTab('orders'); setOrderStatusFilter('new'); } },
                  { l: 'Neue Reservierungen', v: stats.new_reservations, c: 'text-emerald-700', onClick: () => { setActiveTab('reservations'); setResStatusFilter('new'); } },
                  { l: 'Nachrichten', v: stats.new_contacts, c: 'text-sky-700', onClick: () => setActiveTab('contacts') },
                  { l: 'Tisch-Bestellungen', v: stats.new_table_orders, c: 'text-violet-700', onClick: () => setActiveTab('tables') },
                  { l: 'Umsatz heute', v: `${stats.today_revenue?.toFixed(2)} \u20ac`, c: 'text-emerald-700' },
                  { l: 'Umsatz gesamt', v: `${stats.total_revenue?.toFixed(2)} \u20ac`, c: 'text-stone-700' },
                  { l: 'Freie Tische', v: `${stats.free_tables}/${stats.total_tables}`, c: 'text-sky-700' },
                  { l: 'Papierkorb', v: stats.trash_count || 0, c: 'text-stone-500', onClick: () => setActiveTab('trash') },
                ].map((s, i) => (
                  <Card key={i} className={`border-stone-200 ${s.onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={s.onClick}>
                    <CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">{s.l}</p><p className={`text-2xl font-bold tabular-nums ${s.c}`}>{s.v}</p></CardContent>
                  </Card>
                ))}
              </div>
              {stats.popular_items?.length > 0 && (
                <Card className="border-stone-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-stone-600">Beliebteste Gerichte</CardTitle></CardHeader>
                  <CardContent>{stats.popular_items.map((p, i) => <div key={i} className="flex justify-between py-1.5 text-sm"><span className="text-stone-700">{i + 1}. {p.name}</span><span className="text-stone-500 tabular-nums">{p.count}x</span></div>)}</CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ━━━ ORDERS ━━━ */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Bestellungen</h2>
              <FilterBar search={orderSearch} setSearch={setOrderSearch} statusFilter={orderStatusFilter} setStatusFilter={setOrderStatusFilter}
                statusOptions={[{ value: 'new', label: 'Neu' }, { value: 'confirmed', label: 'Bestätigt' }, { value: 'in_zubereitung', label: 'Zubereitung' }, { value: 'ready', label: 'Fertig' }, { value: 'bezahlt_abgeholt', label: 'Bezahlt' }, { value: 'cancelled', label: 'Storniert' }]}
                placeholder="Name, Telefon oder ID..." />
              <div className="space-y-2" data-testid="orders-list">
                {filteredOrders.length === 0 && <p className="text-center text-stone-400 py-8">Keine Bestellungen gefunden</p>}
                {filteredOrders.map(o => {
                  const isExpanded = expandedOrder === o.id;
                  return (
                    <Card key={o.id} className="border-stone-200 overflow-hidden">
                      <div className="p-3 sm:p-4">
                        {/* Order Header Row */}
                        <div className="flex items-start gap-3">
                          <button onClick={() => setExpandedOrder(isExpanded ? null : o.id)} className="mt-0.5 text-stone-400 hover:text-stone-600 transition-colors">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-stone-800 truncate">{o.customer_name}</span>
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatus(o.status).color}`}>{getStatus(o.status).label}</Badge>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{o.fulfillment === 'delivery' ? <><Truck className="w-3 h-3 mr-0.5" />Lieferung</> : <><Package className="w-3 h-3 mr-0.5" />Abholung</>}</Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-500">
                              <span className="tabular-nums font-semibold text-amber-800">{o.total?.toFixed(2)} &euro;</span>
                              <span>{o.items?.length} Artikel</span>
                              <span>#{o.id?.slice(0, 6)}</span>
                              {o.created_at && <span>{new Date(o.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                            </div>
                          </div>
                          <ActionMenu>
                            {o.status === 'new' && <ActionItem icon={Check} label="Bestätigen" onClick={() => handleOrderStatus(o.id, 'confirmed')} variant="success" />}
                            {(o.status === 'new' || o.status === 'confirmed') && <ActionItem icon={Timer} label="Fertigstellungszeit" onClick={() => setTimeModal({ open: true, order: o })} variant="info" />}
                            {o.status !== 'bezahlt_abgeholt' && o.status !== 'cancelled' && <ActionItem icon={CreditCard} label="Bezahlt & abgeholt" onClick={() => handlePaidCollected(o.id)} variant="success" />}
                            {o.status !== 'cancelled' && <ActionItem icon={XCircle} label="Stornieren" onClick={() => handleOrderStatus(o.id, 'cancelled')} variant="danger" />}
                            <Separator className="my-1" />
                            {o.customer_email && <ActionItem icon={Mail} label="E-Mail senden" onClick={() => openEmailFor(o.customer_email, o.customer_name, `Ihre Bestellung #${o.id?.slice(0,6)} bei Little Italy`, `Bestellung #${o.id?.slice(0,6)}`)} />}
                            {o.customer_phone && <ActionItem icon={Phone} label="WhatsApp" onClick={() => window.open(getWhatsAppLinkToCustomer(o.customer_phone, generateOrderConfirmation(o)), '_blank')} />}
                            <Separator className="my-1" />
                            <ActionItem icon={Trash2} label="Löschen" onClick={() => handleDeleteOrder(o.id)} variant="danger" />
                          </ActionMenu>
                        </div>
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-3 ml-7 space-y-3">
                            <div className="bg-stone-50 rounded-lg p-3 space-y-1.5">
                              {o.items?.map((item, i) => <div key={i} className="flex justify-between text-sm"><span>{item.quantity}x {item.name}</span><span className="tabular-nums font-medium">{item.price?.toFixed(2)} &euro;</span></div>)}
                              <Separator />
                              <div className="flex justify-between font-semibold text-sm"><span>Gesamt</span><span className="tabular-nums">{o.total?.toFixed(2)} &euro;</span></div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                              {o.customer_phone && <div className="flex items-center gap-1.5 text-stone-600"><Phone className="w-3 h-3" />{o.customer_phone}</div>}
                              {o.customer_email && <div className="flex items-center gap-1.5 text-stone-600"><Mail className="w-3 h-3" />{o.customer_email}</div>}
                              {o.customer_address && <div className="flex items-center gap-1.5 text-stone-600 col-span-full"><MapPin className="w-3 h-3" />{o.customer_address}</div>}
                              {o.notes && <div className="col-span-full bg-amber-50 rounded-lg p-2 text-amber-800">Notiz: {o.notes}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ━━━ RESERVATIONS ━━━ */}
          {activeTab === 'reservations' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Reservierungen</h2>
              <FilterBar search={resSearch} setSearch={setResSearch} statusFilter={resStatusFilter} setStatusFilter={setResStatusFilter}
                statusOptions={[{ value: 'new', label: 'Neu' }, { value: 'confirmed', label: 'Bestätigt' }, { value: 'cancelled', label: 'Storniert' }, { value: 'done', label: 'Erledigt' }]}
                placeholder="Name, Telefon oder E-Mail..." />
              <div className="space-y-2" data-testid="reservations-list">
                {filteredReservations.length === 0 && <p className="text-center text-stone-400 py-8">Keine Reservierungen</p>}
                {filteredReservations.map(r => (
                  <Card key={r.id} className="border-stone-200">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-stone-800">{r.name}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatus(r.status).color}`}>{getStatus(r.status).label}</Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone-500">
                            <span className="font-medium text-stone-700"><Calendar className="w-3 h-3 inline mr-0.5" />{r.date}</span>
                            <span><Clock className="w-3 h-3 inline mr-0.5" />{r.time}</span>
                            <span><Users className="w-3 h-3 inline mr-0.5" />{r.party_size} Pers.</span>
                            {r.phone && <span><Phone className="w-3 h-3 inline mr-0.5" />{r.phone}</span>}
                          </div>
                          {r.notes && <p className="text-xs text-stone-400 mt-1 truncate">Notiz: {r.notes}</p>}
                        </div>
                        <ActionMenu>
                          {r.status === 'new' && <ActionItem icon={Check} label="Bestätigen" onClick={() => handleResStatus(r.id, 'confirmed')} variant="success" />}
                          {r.status !== 'cancelled' && r.status !== 'done' && <ActionItem icon={Check} label="Erledigt" onClick={() => handleResStatus(r.id, 'done')} variant="success" />}
                          {r.status !== 'cancelled' && <ActionItem icon={XCircle} label="Stornieren" onClick={() => handleResStatus(r.id, 'cancelled')} variant="danger" />}
                          <Separator className="my-1" />
                          {r.email && <ActionItem icon={Mail} label="E-Mail senden" onClick={() => openEmailFor(r.email, r.name, `Ihre Reservierung am ${r.date} bei Little Italy`, `Reservierung ${r.date} ${r.time}`)} />}
                          {r.phone && <ActionItem icon={Phone} label="WhatsApp" onClick={() => window.open(getWhatsAppLinkToCustomer(r.phone, generateReservationConfirmation(r)), '_blank')} />}
                          <Separator className="my-1" />
                          <ActionItem icon={Trash2} label="Löschen" onClick={() => handleDeleteRes(r.id)} variant="danger" />
                        </ActionMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ━━━ TABLES ━━━ */}
          {activeTab === 'tables' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Tischverwaltung</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="tables-grid">
                {tables.map(t => {
                  const hasOrders = t.status !== 'frei' || t.open_total > 0;
                  const statusColors = { frei: 'border-emerald-200 bg-emerald-50/50', bestellt: 'border-amber-200 bg-amber-50/50', besetzt: 'border-sky-200 bg-sky-50/50' };
                  return (
                    <Card key={t.table_number} className={`border-2 cursor-pointer hover:shadow-md transition-all ${statusColors[t.status] || 'border-stone-200'}`}
                      onClick={() => hasOrders ? setBillModal({ open: true, tableNumber: t.table_number }) : null} data-testid={`table-card-${t.table_number}`}>
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-stone-700 tabular-nums">{t.table_number}</p>
                        <Badge variant="outline" className={`text-[10px] mt-1 ${t.status === 'frei' ? 'border-emerald-300 text-emerald-700' : t.status === 'bestellt' ? 'border-amber-300 text-amber-700' : 'border-sky-300 text-sky-700'}`}>{t.status}</Badge>
                        {t.open_total > 0 && (
                          <div className="mt-2 pt-2 border-t border-stone-200">
                            <p className="text-xs text-stone-500">Offen</p>
                            <p className="text-sm font-bold text-amber-800 tabular-nums">{t.remaining_total?.toFixed(2)} &euro;</p>
                            {t.paid_total > 0 && <p className="text-[10px] text-emerald-600">Bezahlt: {t.paid_total?.toFixed(2)} &euro;</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ━━━ CONTACTS ━━━ */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Nachrichten</h2>
              <FilterBar search={contactSearch} setSearch={setContactSearch} placeholder="Name oder E-Mail..."
                statusFilter="all" setStatusFilter={() => {}}
                statusOptions={[{ value: 'new', label: 'Neu' }, { value: 'read', label: 'Gelesen' }, { value: 'replied', label: 'Beantwortet' }]} />
              <div className="space-y-2" data-testid="contacts-list">
                {filteredContacts.length === 0 && <p className="text-center text-stone-400 py-8">Keine Nachrichten</p>}
                {filteredContacts.map(c => (
                  <Card key={c.id} className="border-stone-200">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-stone-800">{c.name}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatus(c.status).color}`}>{getStatus(c.status).label}</Badge>
                          </div>
                          <p className="text-xs text-stone-500 mb-1">{c.email}{c.phone ? ` | ${c.phone}` : ''}</p>
                          <p className="text-sm text-stone-600 line-clamp-2">{c.message}</p>
                        </div>
                        <ActionMenu>
                          {c.status === 'new' && <ActionItem icon={Eye} label="Als gelesen" onClick={async () => { await updateContactStatus(c.id, 'read'); toast.success('Markiert'); loadData(); }} />}
                          <ActionItem icon={Mail} label="Antworten per E-Mail" onClick={() => openEmailFor(c.email, c.name, `Re: Ihre Anfrage an Little Italy`, `Kontaktanfrage von ${c.name}`)} variant="info" />
                          {c.phone && <ActionItem icon={Phone} label="WhatsApp" onClick={() => window.open(getWhatsAppLinkToCustomer(c.phone), '_blank')} />}
                          <Separator className="my-1" />
                          <ActionItem icon={Trash2} label="Löschen" onClick={() => handleDeleteContact(c.id)} variant="danger" />
                        </ActionMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ━━━ MENU ━━━ */}
          {activeTab === 'menu' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-serif text-stone-800">Speisekarte</h2>
                <span className="text-sm text-stone-500">{menuItems.length} Gerichte</span>
              </div>
              <div className="space-y-2" data-testid="menu-list">
                {categories.map(cat => {
                  const catItems = menuItems.filter(m => m.category_id === cat.id);
                  if (catItems.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <h3 className="text-sm font-semibold text-stone-600 mb-2 mt-4">{cat.name} ({catItems.length})</h3>
                      {catItems.map(m => (
                        <Card key={m.id} className="border-stone-200 mb-1.5">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                              {m.image && <img src={m.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className={`text-sm font-medium truncate ${!m.active ? 'text-stone-400 line-through' : 'text-stone-800'}`}>{m.name}</p>
                                <p className="text-xs text-stone-500 tabular-nums">{m.price?.toFixed(2)} &euro;</p>
                              </div>
                            </div>
                            <ActionMenu>
                              <ActionItem icon={m.active ? XCircle : Check} label={m.active ? 'Deaktivieren' : 'Aktivieren'}
                                onClick={async () => { await updateMenuItem(m.id, { active: !m.active }); toast.success('Aktualisiert'); loadData(); }} />
                              <ActionItem icon={Trash2} label="Löschen" onClick={() => handleDeleteMenu(m.id)} variant="danger" />
                            </ActionMenu>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ━━━ REVIEWS ━━━ */}
          {activeTab === 'reviews' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Bewertungen</h2>
              <div className="space-y-2" data-testid="reviews-list">
                {reviews.map(r => (
                  <Card key={r.id} className="border-stone-200">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-stone-800">{r.author}</span>
                            <span className="text-amber-500 text-sm">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                            {!r.active && <Badge variant="outline" className="text-[10px] text-stone-400">Ausgeblendet</Badge>}
                          </div>
                          <p className="text-sm text-stone-600">{r.text}</p>
                          <p className="text-xs text-stone-400 mt-1">{r.date}</p>
                        </div>
                        <ActionMenu>
                          <ActionItem icon={r.active ? XCircle : Check} label={r.active ? 'Ausblenden' : 'Anzeigen'}
                            onClick={async () => { await toggleReview(r.id); loadData(); }} />
                          <ActionItem icon={Trash2} label="Löschen" onClick={() => handleDeleteReview(r.id)} variant="danger" />
                        </ActionMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* ━━━ TRASH / PAPIERKORB ━━━ */}
          {activeTab === 'trash' && (
            <div className="space-y-4" data-testid="trash-section">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-serif text-stone-800 flex items-center gap-2"><Trash2 className="w-5 h-5 text-stone-400" /> Papierkorb</h2>
                {Object.keys(trash).length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleEmptyTrash} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="empty-trash-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Papierkorb leeren
                  </Button>
                )}
              </div>
              {Object.keys(trash).length === 0 && (
                <Card className="border-stone-200"><CardContent className="py-12 text-center"><Trash2 className="w-10 h-10 mx-auto text-stone-300 mb-3" /><p className="text-stone-400">Der Papierkorb ist leer</p></CardContent></Card>
              )}
              {Object.entries(trash).map(([type, items]) => (
                <div key={type}>
                  <h3 className="text-sm font-semibold text-stone-600 mb-2 flex items-center gap-2"><Badge variant="outline">{TRASH_TYPE_LABELS[type] || type}</Badge><span className="text-stone-400 text-xs">{items.length} Elemente</span></h3>
                  <div className="space-y-1.5">
                    {items.map(item => (
                      <Card key={item.id} className="border-stone-200 border-dashed">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="text-sm text-stone-600 truncate">{item.name || item.customer_name || item.author || item.first_name ? `${item.first_name || ''} ${item.last_name || ''}`.trim() : item.id?.slice(0, 8)}</p>
                            <p className="text-xs text-stone-400">{item.deleted_at ? `Gelöscht: ${new Date(item.deleted_at).toLocaleString('de-DE')}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => handleRestoreTrash(type, item.id)} className="h-8 w-8 p-0 text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid={`restore-${item.id}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger><TooltipContent>Wiederherstellen</TooltipContent></Tooltip></TooltipProvider>
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => handlePermanentDelete(type, item.id)} className="h-8 w-8 p-0 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`perm-delete-${item.id}`}>
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger><TooltipContent>Endgültig löschen</TooltipContent></Tooltip></TooltipProvider>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ━━━ QR-CODES ━━━ */}
          {activeTab === 'qrcodes' && (
            <div className="space-y-4" data-testid="qrcodes-section">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-serif text-stone-800 flex items-center gap-2"><QrCode className="w-5 h-5 text-amber-700" /> QR-Code Bestellsystem</h2>
                <Button onClick={() => navigate('/admin/qr-codes')} className="bg-amber-700 hover:bg-amber-800" data-testid="open-qr-print">
                  <QrCode className="w-4 h-4 mr-2" /> QR-Codes drucken
                </Button>
              </div>
              <Card className="border-stone-200">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <QrCode className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-medium text-stone-800 mb-1">So funktioniert es</h3>
                      <ol className="text-sm text-stone-600 space-y-1.5 list-decimal list-inside">
                        <li>Drucken Sie die QR-Codes aus und legen Sie sie auf die Tische</li>
                        <li>Der Gast scannt den Code mit dem Handy</li>
                        <li>Er sieht die Speisekarte und bestellt direkt am Tisch</li>
                        <li>Die Bestellung erscheint sofort in der Tischverwaltung</li>
                        <li>Kein Kellner nötig &ndash; der Gast bestellt selbst!</li>
                      </ol>
                    </div>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                    <div className="bg-stone-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-stone-700">{tables.length}</p>
                      <p className="text-xs text-stone-500">Tische gesamt</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-emerald-700">{tables.filter(t => t.status === 'frei').length}</p>
                      <p className="text-xs text-emerald-600">Frei</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-amber-700">{tables.filter(t => t.status !== 'frei').length}</p>
                      <p className="text-xs text-amber-600">Besetzt / Bestellt</p>
                    </div>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-stone-500 mb-1">Beispiel-Link für Tisch 1</p>
                    <code className="text-sm text-amber-800 font-mono">{window.location.origin}/t/1</code>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ━━━ KASSENBUCH ━━━ */}
          {activeTab === 'kassenbuch' && <KassenbuchTab />}

          {/* ━━━ SETTINGS ━━━ */}
          {activeTab === 'settings' && (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-stone-800">Einstellungen</h2>
              <Card className="border-stone-200"><CardContent className="p-4 text-center text-stone-500"><Settings className="w-8 h-8 mx-auto mb-2 text-stone-300" /><p>Einstellungen werden beibehalten. Kontaktieren Sie den Administrator für Änderungen.</p></CardContent></Card>
            </div>
          )}
        </main>
      </div>

      {/* ━━━ MODALS ━━━ */}
      <EmailComposeModal open={emailModal.open} onClose={() => setEmailModal({ open: false, prefill: {} })} prefill={emailModal.prefill} />
      {timeModal.order && <TimeEstimateModal open={timeModal.open} onClose={() => setTimeModal({ open: false, order: null })} order={timeModal.order} onSend={loadData} />}
      <TableBillModal open={billModal.open} onClose={() => setBillModal({ open: false, tableNumber: null })} tableNumber={billModal.tableNumber} onUpdate={loadData} />

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />{confirmDialog.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">{confirmDialog.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => { confirmDialog.onConfirm?.(); setConfirmDialog({ ...confirmDialog, open: false }); }}>Bestätigen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
